// Label placement for the highlight scenes.
//
// Every overlap in the first implementation came from the same root cause:
// labels were positioned at fixed coordinates while the marks they sit beside
// move with the data. A category label starting at x=5 and a value label whose
// x depends on the value will collide as soon as the value is large enough.
//
// So placement is computed from measured widths, and when there is genuinely
// not enough room the resolution escalates in a fixed, deterministic order:
//
//   1. place outside the mark (preferred — never occludes data)
//   2. place inside the mark, if the mark is wide enough to hold the text
//   3. truncate the category to the measured gutter
//   4. drop the label and record it in `omissions`
//
// Nothing here is random or clock-dependent, so the same evidence always
// produces the same picture — which is what makes the content hash meaningful.

import { measureText, truncateToWidth } from './textMetrics'
import { FONT } from './tokens'

/** Gap kept between any two pieces of text, and between text and a mark. */
export const LABEL_GUTTER = 6

export type RowLabelLayout = {
  /** x where category labels start. */
  categoryX: number
  /** Max width available to a category label. */
  categoryWidth: number
  /** x of the zero baseline / start of the plotting area. */
  plotX: number
  /** Width available for marks. */
  plotWidth: number
  /**
   * Absolute bounds text may occupy. Wider than the plot, because the value
   * gutter reserved on the right is for labels, not for marks — the placement
   * function has to know it exists or it will decide the label does not fit and
   * reverse it out inside the bar instead.
   */
  labelLeftEdge: number
  labelRightEdge: number
}

/**
 * Allocate a left gutter sized to the labels that actually exist, clamped so a
 * single long account name cannot eat the chart. Returns the plot geometry the
 * caller draws into.
 */
export function layoutRowLabels(
  labels: string[],
  options: {
    width: number
    paddingLeft?: number
    paddingRight?: number
    minPlotWidth?: number
    maxGutterRatio?: number
    /**
     * The value strings that will be drawn at the end of each bar. When given,
     * the plot is narrowed by the widest of them so every value label fits
     * OUTSIDE its bar. Without this the placement falls back per row — long
     * bars get a label inside in reversed-out text, short bars get one outside
     * — and the value column reads as two different treatments in one chart.
     */
    valueLabels?: string[]
  },
): RowLabelLayout {
  const paddingLeft = options.paddingLeft ?? 4
  const paddingRight = options.paddingRight ?? 4
  const minPlotWidth = options.minPlotWidth ?? 120
  const maxGutterRatio = options.maxGutterRatio ?? 0.42

  const widest = labels.reduce(
    (max, label) => Math.max(max, measureText(label, { size: FONT.category.size, weight: FONT.category.weight })),
    0,
  )
  const widestValue = (options.valueLabels ?? []).reduce(
    (max, label) => Math.max(max, measureText(label, { size: FONT.value.size, weight: FONT.value.weight, tabular: true })),
    0,
  )
  const valueGutter = widestValue > 0 ? widestValue + LABEL_GUTTER : 0

  const available = options.width - paddingLeft - paddingRight - valueGutter
  const ceiling = Math.min(available * maxGutterRatio, available - minPlotWidth)
  const categoryWidth = Math.max(0, Math.min(widest, Math.max(0, ceiling)))
  const plotX = paddingLeft + categoryWidth + LABEL_GUTTER

  return {
    categoryX: paddingLeft,
    categoryWidth,
    plotX,
    plotWidth: Math.max(0, options.width - paddingRight - valueGutter - plotX),
    labelLeftEdge: plotX,
    labelRightEdge: options.width - paddingRight,
  }
}

/** Category text trimmed to the allocated gutter, measured not guessed. */
export function categoryLabel(label: string, layout: RowLabelLayout): string {
  return truncateToWidth(label, layout.categoryWidth, { size: FONT.category.size, weight: FONT.category.weight })
}

export type ValueLabelPlacement = {
  x: number
  anchor: 'start' | 'end' | 'middle'
  /** True when the label sits on top of the mark and needs contrast treatment. */
  inside: boolean
  /** False when there was no room at all; the caller must not draw it. */
  visible: boolean
}

/**
 * Place a value label for a horizontal bar running from `baseX` to `endX`.
 *
 * Prefers the outside edge so the mark is never occluded. Falls back to inside
 * the bar when the outside would run past the chart edge, and reports
 * `visible: false` when neither fits — at which point the caller records an
 * omission rather than drawing text on top of other text.
 */
export function placeBarValueLabel(
  value: string,
  options: { baseX: number; endX: number; layout: RowLabelLayout },
): ValueLabelPlacement {
  const width = measureText(value, { size: FONT.value.size, weight: FONT.value.weight, tabular: true })
  const rightEdge = options.layout.labelRightEdge
  const leftEdge = options.layout.labelLeftEdge - LABEL_GUTTER
  const pointsRight = options.endX >= options.baseX

  if (pointsRight) {
    if (options.endX + LABEL_GUTTER + width <= rightEdge) {
      return { x: options.endX + LABEL_GUTTER, anchor: 'start', inside: false, visible: true }
    }
    if (options.endX - options.baseX >= width + LABEL_GUTTER * 2) {
      return { x: options.endX - LABEL_GUTTER, anchor: 'end', inside: true, visible: true }
    }
    return { x: options.endX, anchor: 'end', inside: false, visible: false }
  }

  if (options.endX - LABEL_GUTTER - width >= leftEdge) {
    return { x: options.endX - LABEL_GUTTER, anchor: 'end', inside: false, visible: true }
  }
  if (options.baseX - options.endX >= width + LABEL_GUTTER * 2) {
    return { x: options.endX + LABEL_GUTTER, anchor: 'start', inside: true, visible: true }
  }
  return { x: options.endX, anchor: 'start', inside: false, visible: false }
}

/**
 * Stride for thinning a categorical axis so adjacent ticks never touch.
 * Derived from the widest label, so it is stable under a single value changing
 * — a stride computed from the *current* label would flicker between renders.
 */
export function tickStride(labels: string[], available: number, size = FONT.axis.size): number {
  if (labels.length <= 1) return 1
  const widest = labels.reduce((max, label) => Math.max(max, measureText(label, { size, weight: FONT.axis.weight })), 0)
  const slot = available / labels.length
  if (slot <= 0) return labels.length
  return Math.max(1, Math.ceil((widest + LABEL_GUTTER) / slot))
}

/**
 * Resolve vertical collisions in a set of point labels by nudging them apart,
 * preserving order. Used by line/scatter scenes where two series end close
 * together. Deterministic: a stable sort by y, then a single forward pass.
 */
export function spreadVertically(
  entries: Array<{ key: string; y: number }>,
  options: { minGap: number; top: number; bottom: number },
): Map<string, number> {
  const sorted = [...entries].sort((a, b) => a.y - b.y || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0))
  const placed = new Map<string, number>()
  if (!sorted.length) return placed

  const top = options.top
  const bottom = Math.max(top, options.bottom)
  const available = bottom - top
  const minGap = sorted.length > 1
    ? Math.min(Math.max(0, options.minGap), available / (sorted.length - 1))
    : 0
  const ys: number[] = []
  let cursor = top
  for (const entry of sorted) {
    const y = Math.max(entry.y, cursor)
    ys.push(y)
    cursor = y + minGap
  }

  // A forward-only pass merely clamps every late label to `bottom`, recreating
  // the collision at the lower edge. Back-propagate the overflow so the whole
  // ordered group keeps its gap inside the available rail.
  if (ys[ys.length - 1] > bottom) {
    ys[ys.length - 1] = bottom
    for (let index = ys.length - 2; index >= 0; index -= 1) {
      ys[index] = Math.min(ys[index], ys[index + 1] - minGap)
    }
  }
  if (ys[0] < top) {
    const shift = top - ys[0]
    for (let index = 0; index < ys.length; index += 1) ys[index] += shift
  }

  sorted.forEach((entry, index) => {
    placed.set(entry.key, Math.max(top, Math.min(ys[index], bottom)))
  })
  return placed
}
